import { useState, useMemo, useRef, useEffect } from 'react';
import { Filter, Search, X, Check } from 'lucide-react';

const FALLBACK_COLOR = '#636df1';

export interface FilterDropdownProps {
  /** Label for the filter (e.g., "Teams", "Features") */
  label: string;
  /** Available options to select from */
  options: string[];
  /** Currently selected options */
  selected: Set<string>;
  /** Callback when selection changes */
  onChange: (selected: Set<string>) => void;
  /** Optional color map for options (displays color swatch) */
  colorMap?: Record<string, string>;
  /** Optional function to get display label for an option (defaults to identity) */
  getDisplayLabel?: (option: string) => string;
}

/**
 * Generic searchable multi-select dropdown molecule
 *
 * Provides filtering, select all/deselect all, and optional color swatches.
 * Used for team, feature, and other multi-select filters.
 */
export function FilterDropdown({
  label,
  options,
  selected,
  onChange,
  colorMap,
  getDisplayLabel = (o) => o,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter options by search query (search against display labels)
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) => getDisplayLabel(option).toLowerCase().includes(query));
  }, [options, searchQuery, getDisplayLabel]);

  const toggleOption = (option: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(option)) {
      newSelected.delete(option);
    } else {
      newSelected.add(option);
    }
    onChange(newSelected);
  };

  const selectAll = () => onChange(new Set(filteredOptions));
  const deselectAll = () => onChange(new Set());

  const getLabel = () => {
    if (selected.size === 0) return 'None';
    if (selected.size === options.length) return `All ${label}`;
    return `${selected.size} ${label.toLowerCase()}`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          setSearchQuery('');
        }}
        className="
          flex items-center gap-2 rounded-md border border-border-default bg-bg-elevated px-3 py-1.5 text-sm
          text-text-primary transition-colors
          hover:bg-bg-base
          focus:ring-2 focus:ring-primary-500 focus:outline-hidden
        "
      >
        <Filter className="size-4 text-text-muted" />
        <span>{getLabel()}</span>
      </button>

      {isOpen && (
        <div
          className="
            absolute left-0 z-50 mt-2 flex max-h-96 w-64 flex-col overflow-hidden rounded-lg border
            border-border-default bg-bg-elevated shadow-lg
          "
        >
          {/* Search Input */}
          <div className="border-b border-border-default p-3">
            <div className="relative">
              <Search className="absolute top-1/2 left-2 size-4 -translate-y-1/2 transform text-text-subtle" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="
                  w-full rounded-md border border-border-strong bg-transparent py-1.5 pr-3 pl-8 text-sm
                  text-text-primary
                  focus:ring-2 focus:ring-primary-500 focus:outline-hidden
                "
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="
                    absolute top-1/2 right-2 -translate-y-1/2 transform text-text-subtle
                    hover:text-text-secondary
                  "
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
          </div>

          {/* Select All / Deselect All */}
          <div className="flex gap-2 border-b border-border-default px-3 py-2">
            <button
              onClick={selectAll}
              className="
                text-xs text-action-primary
                hover:underline
              "
            >
              Select All
            </button>
            <span className="text-text-disabled">|</span>
            <button
              onClick={deselectAll}
              className="
                text-xs text-action-primary
                hover:underline
              "
            >
              Deselect All
            </button>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-text-muted">No {label.toLowerCase()} found</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selected.has(option);
                return (
                  <label
                    key={option}
                    className="
                      flex cursor-pointer items-center gap-2 px-3 py-2
                      hover:bg-bg-base
                    "
                  >
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOption(option)}
                        className="sr-only"
                      />
                      <div
                        className={`
                          flex size-4 items-center justify-center rounded-sm border-2 transition-colors
                          ${isSelected ? 'border-action-primary bg-action-primary' : `border-border-strong`}
                        `}
                      >
                        {isSelected && <Check className="size-3 text-text-inverse" />}
                      </div>
                    </div>
                    <span className="flex-1 text-sm text-text-primary">{getDisplayLabel(option)}</span>
                    {colorMap && (
                      <div
                        className="size-3 rounded-full"
                        style={{ backgroundColor: colorMap[option] ?? FALLBACK_COLOR }}
                      />
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
